import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Instagram, Linkedin, Building2, Mail } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

const SOCIALS = [
  { label: "Instagram", href: "https://www.instagram.com/amiran_azaladze/", Icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/amirani/", Icon: Linkedin },
  { label: "X", href: "https://x.com/AmiranAzaladze", Icon: XIcon },
  { label: "Crunchbase", href: "https://www.crunchbase.com/person/amiran-azaladze", Icon: Building2 },
];

const line = "absolute left-1/2 top-1/2 h-[2px] w-6 -ml-3 rounded-full bg-ink";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.12 } },
    exit: {},
  };
  const item = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: 12, transition: { duration: 0.2 } },
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled || open ? "bg-obsidian/70 backdrop-blur-xl border-b border-white/5" : "border-b border-transparent"
        } ${open ? "!bg-transparent !border-transparent !backdrop-blur-0" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <a href="#hero" data-testid="nav-logo" className="font-display text-lg tracking-[0.06em] font-bold relative z-[60]">
            AMIRAN <span className="text-amber">AZALADZE</span>
          </a>
          <ul className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="text-[0.7rem] font-medium tracking-[0.18em] uppercase text-ink-secondary hover:text-ink transition-colors duration-300"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-amber px-5 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-obsidian hover:bg-amber-hover transition-colors duration-300"
          >
            Work With Me
          </a>

          {/* Animated burger */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden relative z-[60] h-11 w-11 -mr-2 flex items-center justify-center"
          >
            <motion.span
              className={line}
              animate={open ? { rotate: 45, y: 0, width: 26 } : { rotate: 0, y: -5, width: 24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.span
              className={line}
              animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              className={line}
              animate={open ? { rotate: -45, y: 0, width: 26 } : { rotate: 0, y: 5, width: 18 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            />
          </button>
        </div>
      </nav>

      {/* Full-screen mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 md:hidden overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3, delay: 0.1 } }}
          >
            {/* background layers */}
            <motion.div
              className="absolute inset-0 bg-obsidian"
              initial={{ clipPath: "circle(0% at calc(100% - 42px) 36px)" }}
              animate={{ clipPath: "circle(150% at calc(100% - 42px) 36px)" }}
              exit={{ clipPath: "circle(0% at calc(100% - 42px) 36px)" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(232,98,26,0.20),transparent_55%)]" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber/10 blur-3xl" />

            <motion.div
              className="relative h-full flex flex-col justify-center px-8 pt-24 pb-10"
              variants={container}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              <motion.div variants={item} className="text-[0.66rem] uppercase tracking-[0.28em] text-ink-muted mb-8">
                Menu
              </motion.div>

              <ul className="space-y-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.li key={l.href} variants={item} className="overflow-hidden">
                    <a
                      href={l.href}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      onClick={() => setOpen(false)}
                      className="group flex items-center gap-4 py-1.5"
                    >
                      <span className="font-mono text-xs text-amber/70 w-7 pt-1.5">0{i + 1}</span>
                      <span className="font-display text-[2.6rem] leading-[1.05] font-bold text-ink transition-colors duration-300 group-hover:text-amber group-active:text-amber">
                        {l.label}
                      </span>
                      <ArrowUpRight
                        size={22}
                        className="text-amber opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      />
                    </a>
                  </motion.li>
                ))}
              </ul>

              <motion.a
                variants={item}
                href="#contact"
                data-testid="mobile-menu-cta"
                onClick={() => setOpen(false)}
                className="mt-10 inline-flex w-fit items-center gap-2 rounded-full bg-amber px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-obsidian"
              >
                Work With Me
                <ArrowUpRight size={16} />
              </motion.a>

              <motion.div variants={item} className="mt-auto pt-10 border-t border-white/10">
                <a
                  href="mailto:a.azaladze@menelausholding.com"
                  className="flex items-center gap-3 text-ink-secondary text-sm mb-5"
                >
                  <Mail size={15} className="text-amber" />
                  a.azaladze@menelausholding.com
                </a>
                <div className="flex items-center gap-3">
                  {SOCIALS.map(({ label, href, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="h-11 w-11 rounded-full border border-white/12 flex items-center justify-center text-ink-secondary hover:text-obsidian hover:bg-amber hover:border-amber transition-colors duration-300"
                    >
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
